// src/components/PlantingForm.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Box,
} from "@mui/material";
import apiClient from "../utils/apiClient";
import "../style/Crops.css";
import MonthBoxGrid from "../components/MonthBoxGrid";
import SoilIndicator from "./SoilIndicator";

const monthNames = [
  "Jan","Feb","Mar","Apr","May","Jun",
  "Jul","Aug","Sep","Oct","Nov","Dec"
];

const getMonthIndex = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date.getMonth();
};

const inRange = (start, end, month) => {
  if (start === null || end === null || month === null) return false;
  if (start <= end) return month >= start && month <= end;
  return month >= start || month <= end;
};

const PlantingForm = ({ open, onClose, plots, onSave, garden_uuid, planted, plot_group_uuid = null, cropTypeFilter = null }) => {
  const [formState, setFormState] = useState({
    plot_uuid: "",
    crop_uuid: "",
    date_planted: "",
    amount: "",
    method: "",
    orientation: "",
    placement_mode: "",
  });

  const [crops, setCrops] = useState([]);
  const [cropSuitability, setCropSuitability] = useState([]);
  const [plotSuitability, setPlotSuitability] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [occupancy, setOccupancy] = useState([]);
  const [plotHasCrops, setPlotHasCrops] = useState(false);
  const [selectionMode, setSelectionMode] = useState("plot-first"); // "plot-first" or "crop-first"
  const [currentPage, setCurrentPage] = useState(1); // Pagination: 1, 2, or 3
  const [isMobile, setIsMobile] = useState(false);
  const isEditMode = Boolean(planted && planted.planted_uuid);
  
  // Detect mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  
  // Check if we're editing the only crop in the plot
  // (occupancy includes the crop being edited, so we check if there are no other crops)
  const isEditingOnlyCrop = isEditMode && planted?.planted_uuid && 
    occupancy.filter(o => o.planted.planted_uuid !== planted.planted_uuid).length === 0;

  useEffect(() => {
    const fetchCrops = async () => {
      try {
        const response = await apiClient.get("/crops");
        // Filter crops based on cropTypeFilter prop
        let filteredCrops = response.data;
        if (cropTypeFilter === 'vegetable') {
          // For vegetables: show vegetables and cover crops (exclude fruits and herbs)
          filteredCrops = response.data.filter(crop => 
            crop.crop_type?.name === "Vegetable" || crop.crop_type?.name === "Cover crop"
          );
        } else if (cropTypeFilter === 'herbs-berries') {
          // For herbs: show herbs only
          filteredCrops = response.data.filter(crop => 
            crop.crop_type?.name === "Herb"
          );
        } else {
          // Default: show vegetables and cover crops (exclude fruits and herbs)
          filteredCrops = response.data.filter(crop => 
            crop.crop_type?.name === "Vegetable" || crop.crop_type?.name === "Cover crop"
          );
        }
        setCrops(filteredCrops);
      } catch (err) {
        console.error("Failed to fetch crops:", err);
      }
    };
    fetchCrops();
  }, [cropTypeFilter]);

  // Fetch crop suitability when plot is selected (plot-first mode)
  useEffect(() => {
    const fetchCropSuitability = async () => {
      if (formState.plot_uuid && selectionMode === "plot-first") {
        try {
          // Use the planting year from the form, or current year as fallback
          const plantingYear = formState.date_planted 
            ? new Date(formState.date_planted).getFullYear()
            : new Date().getFullYear();
          
          // If plot_group_uuid is provided, use rotation-filtered endpoint
          if (plot_group_uuid) {
            const response = await apiClient.get(
              `/plots/${formState.plot_uuid}/rotation-filtered-crops?year=${plantingYear}&plot_group_uuid=${plot_group_uuid}`
            );
            // Convert to CropSuitability format for consistency
            const suitabilityData = response.data.map(crop => ({
              crop: crop,
              rotation_allowed: true, // All returned crops are allowed
              rotation_score: null,
              soil_indicator: "neutral" // Could be enhanced later
            }));
            setCropSuitability(suitabilityData);
          } else {
            // Use regular suitable-crops endpoint
            const response = await apiClient.get(`/plots/${formState.plot_uuid}/suitable-crops?year=${plantingYear}`);
            setCropSuitability(response.data);
          }
        } catch (err) {
          console.error("Failed to fetch crop suitability:", err);
          setCropSuitability([]);
        }
      } else {
        setCropSuitability([]);
      }
    };
    fetchCropSuitability();
  }, [formState.plot_uuid, formState.date_planted, selectionMode, plot_group_uuid]);

  useEffect(() => {
    if (!open) {
      setSelectionMode("plot-first");
      setCurrentPage(1); // Reset to page 1 when modal closes
    } else {
      setCurrentPage(1); // Reset to page 1 when modal opens
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    console.log(
      `PlantingForm mode: ${isEditMode ? "edit existing crop" : "plan new crop"}`,
      isEditMode ? planted?.planted_uuid : null
    );
  }, [open, isEditMode, planted]);

  // Fetch plot suitability when crop is selected (crop-first mode)
  useEffect(() => {
    const fetchPlotSuitability = async () => {
      if (formState.crop_uuid && formState.date_planted && selectionMode === "crop-first") {
        try {
          const plantingYear = new Date(formState.date_planted).getFullYear();
          const response = await apiClient.get(`/crops/${formState.crop_uuid}/suitable-plots?year=${plantingYear}&garden_uuid=${garden_uuid}`);
          setPlotSuitability(response.data);
        } catch (err) {
          console.error("Failed to fetch plot suitability:", err);
          setPlotSuitability([]);
        }
      } else {
        setPlotSuitability([]);
      }
    };
    fetchPlotSuitability();
  }, [formState.crop_uuid, formState.date_planted, selectionMode, garden_uuid]);

  useEffect(() => {
    if (!open) return; // Only initialize when dialog opens
    
    if (planted && planted.planted_uuid) {
      // Edit mode: populate form with existing planted crop data
      setFormState({
        plot_uuid: planted.plot_uuid || "",
        crop_uuid: planted.crop_uuid || "",
        date_planted: planted.date_planted || "",
        amount: planted.amount || "",
        method: planted.method || "",
        orientation: planted.orientation || "",
        placement_mode: planted.placement_mode || "",
      });
    } else if (planted && planted.plot_uuid) {
      // New crop for specific plot: set plot and date, but leave crop empty
      setFormState({
        plot_uuid: planted.plot_uuid || "",
        crop_uuid: "",
        date_planted: planted.date_planted || "",
        amount: "",
        method: "",
        orientation: "",
        placement_mode: "",
      });
    } else {
      // New crop: reset form
      setFormState({
        plot_uuid: "",
        crop_uuid: "",
        date_planted: "",
        amount: "",
        method: "",
        orientation: "",
        placement_mode: "",
      });
    }
  }, [open, planted]);

  useEffect(() => {
    if (planted && planted.planted_uuid) {
      setSelectionMode("plot-first");
    }
  }, [planted]);

  useEffect(() => {
    const fetchOccupancy = async () => {
      if (formState.plot_uuid && formState.date_planted) {
        try {
          const res = await apiClient.get(
            `/plots/${formState.plot_uuid}/occupancy`,
            { params: { date: formState.date_planted } }
          );
          const occ = res.data.occupied || [];
          setOccupancy(occ);
          
          // If editing, exclude the current crop from the count
          const otherCrops = isEditMode && planted?.planted_uuid
            ? occ.filter(o => o.planted.planted_uuid !== planted.planted_uuid)
            : occ;
          
          setPlotHasCrops(otherCrops.length > 0);

          // Only set orientation from occupancy if we're not in edit mode
          // (in edit mode, orientation should come from the planted crop being edited)
          if (otherCrops.length > 0 && otherCrops[0].planted.orientation && !isEditMode) {
            setFormState((prev) => ({
              ...prev,
              orientation: otherCrops[0].planted.orientation,
            }));
          }
        } catch (err) {
          console.error("Failed to fetch occupancy:", err);
          setOccupancy([]);
          setPlotHasCrops(false);
        }
      } else {
        setOccupancy([]);
        setPlotHasCrops(false);
      }
    };
    fetchOccupancy();
  }, [formState.plot_uuid, formState.date_planted, isEditMode, planted?.planted_uuid]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const getWindowDates = (crop, periodType) => {
    const windows = (crop.crop_windows || []).filter(w => w.period_type === periodType);
    if (windows.length === 0) return { start: null, end: null };
    // For now, use first window (could be enhanced to pick best window based on date)
    return {
      start: getMonthIndex(windows[0].window_start),
      end: getMonthIndex(windows[0].window_end)
    };
  };

  const handleCropSelect = (crop) => {
    const selectedMonth = formState.date_planted
      ? new Date(formState.date_planted).getMonth()
      : null;

    const planting = getWindowDates(crop, 'planting');
    const sowing = getWindowDates(crop, 'direct_sowing');

    const canPlant = inRange(planting.start, planting.end, selectedMonth);
    const canSow = inRange(sowing.start, sowing.end, selectedMonth);

    let newMethod = formState.method;
    if (selectedMonth !== null) {
      if (canPlant && !canSow) newMethod = "planting";
      else if (!canPlant && canSow) newMethod = "direct_sowing";
    }

    setFormState((prev) => {
      const newState = {
        ...prev,
        crop_uuid: crop.crop_uuid,
        method: newMethod,
      };
      return newState;
    });
    
    // Update selection mode if this is the first selection
    handleCropChange(crop.crop_uuid);
  };


  // Auto-detect selection mode based on what user selects first
  const handlePlotChange = (plotUuid) => {
    setFormState(prev => ({ ...prev, plot_uuid: plotUuid }));
    if (plotUuid && !formState.crop_uuid) {
      setSelectionMode("plot-first");
    }
  };

  const handleCropChange = (cropUuid) => {
    setFormState(prev => ({ ...prev, crop_uuid: cropUuid }));
    if (cropUuid && !formState.plot_uuid) {
      setSelectionMode("crop-first");
    }
  };

  // Helper to get month and year from date_planted
  const getMonthFromDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "" : date.getMonth().toString();
  };

  const getYearFromDate = (dateString) => {
    if (!dateString) return new Date().getFullYear().toString();
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? new Date().getFullYear().toString() : date.getFullYear().toString();
  };

  // Helper to create date from month and year (sets to 1st of month)
  const createDateFromMonthYear = (month, year) => {
    // month can be "0" (January), so check for !== "" instead of truthy
    if (month === "" || !year) return "";
    const monthIndex = parseInt(month, 10);
    const yearNum = parseInt(year, 10);
    if (isNaN(monthIndex) || isNaN(yearNum)) return "";
    // Format as YYYY-MM-DD (monthIndex is 0-11, so add 1 for display)
    const monthStr = String(monthIndex + 1).padStart(2, '0');
    return `${yearNum}-${monthStr}-01`;
  };

  const handleMonthChange = (month) => {
    // Always use current year as default if no year is set
    const currentYear = formState.date_planted 
      ? getYearFromDate(formState.date_planted)
      : new Date().getFullYear().toString();
    // month can be "0" (January) which is falsy, so check for !== ""
    const newDate = month !== "" ? createDateFromMonthYear(month, currentYear) : "";
    setFormState(prev => ({ ...prev, date_planted: newDate }));
    // If we have both crop and date but no plot, switch to crop-first mode
    if (formState.crop_uuid && newDate && !formState.plot_uuid) {
      setSelectionMode("crop-first");
    }
  };

  const handleYearChange = (year) => {
    const currentMonth = getMonthFromDate(formState.date_planted);
    // Only create date if month is also selected (month can be "0" for January)
    const newDate = currentMonth !== "" ? createDateFromMonthYear(currentMonth, year) : "";
    setFormState(prev => ({ ...prev, date_planted: newDate }));
    // If we have both crop and date but no plot, switch to crop-first mode
    if (formState.crop_uuid && newDate && !formState.plot_uuid) {
      setSelectionMode("crop-first");
    }
  };

  const getMaxAmount = () => {
    const plot = plots.find((p) => p.plot_uuid === formState.plot_uuid);
    const crop = crops.find((c) => c.crop_uuid === formState.crop_uuid);

    if (!plot || !crop || !formState.orientation) return null;

    const plotWidth = plot.unit === "meters" ? plot.width : plot.width * 0.3048;
    const plotDepth = plot.unit === "meters" ? plot.depth : plot.depth * 0.3048;

    const longSide = Math.max(plotWidth, plotDepth);
    const shortSide = Math.min(plotWidth, plotDepth);

    const rowSpacing = crop.row_spacing_cm
      ? crop.row_spacing_cm / 100
      : 0.25; // default 25 cm

    const plantSpacing = crop.plant_spacing_cm
      ? crop.plant_spacing_cm / 100
      : 0.1; // default 10 cm

    const limitingDimension =
      formState.orientation === "lengthwise" ? shortSide : longSide;

    const maxRowsFull = Math.max(1, Math.floor(limitingDimension / rowSpacing));

    // Calculate plants per row (along the width dimension)
    const rowLength = formState.orientation === "lengthwise" ? longSide : shortSide;
    const plantsPerRow = Math.floor(rowLength / plantSpacing);

    let availableRows = maxRowsFull;
    
    // Handle placement modes when there are existing crops
    if (plotHasCrops && occupancy.length > 0 && formState.placement_mode) {
      const usedRows = occupancy.reduce(
        (sum, occ) => sum + (occ.planted.rows || 0),
        0
      );
      
      if (formState.placement_mode === "next_to") {
        // Next to: subtract used rows from max
        availableRows = Math.max(0, maxRowsFull - usedRows);
      } else if (formState.placement_mode === "between") {
        // Between: can plant between existing rows
        // If there are N existing rows, we can plant N+1 rows between them
        // But still limited by maxRowsFull
        availableRows = Math.min(maxRowsFull, usedRows + 1);
      }
    }

    const maxAmount = availableRows * plantsPerRow;

    return {
      maxAmount: maxAmount,
      equivalentRows: availableRows
    };
  };

  const calculateRowsFromAmount = (amount) => {
    const plot = plots.find((p) => p.plot_uuid === formState.plot_uuid);
    const crop = crops.find((c) => c.crop_uuid === formState.crop_uuid);

    if (!plot || !crop || !formState.orientation || !amount) return null;

    const plotWidth = plot.unit === "meters" ? plot.width : plot.width * 0.3048;
    const plantSpacing = crop.plant_spacing_cm
      ? crop.plant_spacing_cm / 100
      : 0.1; // default 10 cm

    const rowLength = formState.orientation === "lengthwise" 
      ? Math.max(plotWidth, plot.unit === "meters" ? plot.depth : plot.depth * 0.3048)
      : Math.min(plotWidth, plot.unit === "meters" ? plot.depth : plot.depth * 0.3048);
    
    const plantsPerRow = Math.floor(rowLength / plantSpacing);
    if (plantsPerRow === 0) return null;

    return Math.ceil(amount / plantsPerRow);
  };

  const normalizePayload = (data) => {
  const normalized = { ...data };
  Object.keys(normalized).forEach((key) => {
    if (normalized[key] === "" || normalized[key] === null) {
      delete normalized[key];
    }
  });
  return normalized;
};

const isPage1Complete = () => {
  // Check if page 1 fields are filled: plot, date, orientation (and placement mode if needed)
  // Orientation can be auto-set if plotHasCrops, so we check for either orientation OR plotHasCrops
  if (!formState.plot_uuid || !formState.date_planted) {
    return false;
  }
  // Orientation is required, but it can be auto-set from existing crops
  // If plot has crops, orientation will be set automatically, so we can proceed
  // Otherwise, we need orientation to be explicitly set
  if (!formState.orientation && !plotHasCrops) {
    return false;
  }
  return true;
};

const isPage2Complete = () => {
  // Check if page 2 fields are filled: crop selected
  return !!formState.crop_uuid;
};

const isFormValid = () => {
  if (!formState.plot_uuid || !formState.crop_uuid || !formState.date_planted) {
    return false;
  }
  if (!formState.method) {
    return false;
  }
  if (!formState.amount) {
    return false;
  }
  if (formState.amount) {
    const amountInt = parseInt(formState.amount, 10);
    // Only validate that amount is a positive number, allow values above max
    if (isNaN(amountInt) || amountInt < 1) {
      return false;
    }
  }
  return true;
};

const handleSubmit = async () => {
  try {
    const payload = normalizePayload(formState);
    if (!plotHasCrops) {
      delete payload.placement_mode;
    }
    if (!payload.method) delete payload.method;
    // Remove rows from payload - backend will calculate it from amount
    delete payload.rows;

    if (planted && planted.planted_uuid) {
      await apiClient.put(
        `/gardens/${garden_uuid}/crops/${planted.planted_uuid}`,
        payload
      );
    } else {
      await apiClient.post(`/gardens/${garden_uuid}/crops/`, payload);
    }
    onSave();
    onClose();
  } catch (err) {
    console.error("Failed to save planted crop:", err);
  }
};


  const handleDelete = async () => {
    if (!isEditMode) return;
    try {
      await apiClient.delete(
        `/gardens/${garden_uuid}/crops/${planted.planted_uuid}`
      );
      onSave();
      onClose();
    } catch (err) {
      console.error("Failed to delete planted crop:", err);
    }
  };

  const isSubmitEnabled =
    formState.plot_uuid &&
    formState.crop_uuid &&
    formState.date_planted &&
    (formState.rows || formState.amount);

  const selectedMonth = formState.date_planted
    ? new Date(formState.date_planted).getMonth()
    : null;

  // Helper function to filter crops by cropTypeFilter
  const filterByCropType = (crop) => {
    if (!cropTypeFilter) {
      // Default: show vegetables and cover crops (exclude fruits and herbs)
      return crop.crop_type?.name === "Vegetable" || crop.crop_type?.name === "Cover crop";
    }
    
    if (cropTypeFilter === 'vegetable') {
      // For vegetables: show vegetables and cover crops (exclude fruits and herbs)
      return crop.crop_type?.name === "Vegetable" || crop.crop_type?.name === "Cover crop";
    } else if (cropTypeFilter === 'herbs-berries') {
      // For herbs: show herbs only
      return crop.crop_type?.name === "Herb";
    }
    
    return true;
  };

  // Use suitability data if available, otherwise fall back to basic filtering
  const filteredCrops = cropSuitability.length > 0 
    ? cropSuitability
        .filter((suitability) => {
          const crop = suitability.crop;
          
          // Apply cropTypeFilter first
          if (!filterByCropType(crop)) {
            return false;
          }
          
          const matchesSearch = crop.name.toLowerCase().includes(searchQuery.toLowerCase());
          
          // Check timing validity
          const planting = getWindowDates(crop, 'planting');
          const sowing = getWindowDates(crop, 'direct_sowing');
          const setting = getWindowDates(crop, 'setting');

          let valid = true;
          if (selectedMonth !== null) {
            if (formState.method === "planting") {
              valid = inRange(planting.start, planting.end, selectedMonth);
            } else if (formState.method === "direct_sowing") {
              valid = inRange(sowing.start, sowing.end, selectedMonth);
            } else if (formState.method === "setting") {
              valid = inRange(setting.start, setting.end, selectedMonth);
            } else {
              valid =
                inRange(planting.start, planting.end, selectedMonth) ||
                inRange(sowing.start, sowing.end, selectedMonth) ||
                inRange(setting.start, setting.end, selectedMonth);
            }
          }

          return matchesSearch && valid;
        })
        .map(suitability => suitability.crop) // Extract crop from suitability
    : crops.filter((crop) => {
        // Apply cropTypeFilter first (crops array is already filtered in fetchCrops, but double-check here)
        if (!filterByCropType(crop)) {
          return false;
        }
        
        const planting = getWindowDates(crop, 'planting');
        const sowing = getWindowDates(crop, 'direct_sowing');
        const setting = getWindowDates(crop, 'setting');

        let valid = true;
        if (selectedMonth !== null) {
          if (formState.method === "planting") {
            valid = inRange(planting.start, planting.end, selectedMonth);
          } else if (formState.method === "direct_sowing") {
            valid = inRange(sowing.start, sowing.end, selectedMonth);
          } else if (formState.method === "setting") {
            valid = inRange(setting.start, setting.end, selectedMonth);
          } else {
            valid =
              inRange(planting.start, planting.end, selectedMonth) ||
              inRange(sowing.start, sowing.end, selectedMonth) ||
              inRange(setting.start, setting.end, selectedMonth);
          }
        }

        const matchesSearch = crop.name.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesSearch && valid;
      });


  // Determine if we should show pagination (mobile only)
  const showPagination = isMobile && !isEditMode;
  // Determine which page to show (on mobile, use currentPage; on desktop or edit mode, show all)
  const showPage1 = !showPagination || currentPage === 1;
  const showPage2 = !showPagination || currentPage === 2;
  const showPage3 = !showPagination || currentPage === 3;
  
  // Helper functions for page 3 summary
  const formatMethod = (method) => {
    if (!method) return "";
    const methodMap = {
      "direct_sowing": "Direct Sowing",
      "planting": "Planting",
      "setting": "Setting"
    };
    return methodMap[method] || method;
  };
  
  const formatOrientation = (orientation) => {
    if (!orientation) return "";
    return orientation.charAt(0).toUpperCase() + orientation.slice(1);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };
  
  const getSummaryText = () => {
    const selectedPlot = plots.find((p) => p.plot_uuid === formState.plot_uuid);
    const selectedCrop = crops.find((c) => c.crop_uuid === formState.crop_uuid);
    
    if (!selectedPlot || !selectedCrop || !formState.date_planted || !formState.orientation) {
      return "";
    }
    
    const method = formatMethod(formState.method);
    const cropName = selectedCrop.name;
    const plotName = selectedPlot.name;
    const date = formatDate(formState.date_planted);
    const orientation = formatOrientation(formState.orientation);
    
    let summary = `${cropName} in ${plotName} on ${date} ${orientation}`;
    if (method) {
      summary = `${method} ${summary}`;
    }
    
    return summary;
  };

  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth 
      fullScreen={isMobile}
      className="PlantingForm planting-form-modal"
    >
      <DialogTitle>{isEditMode ? "Edit Planned Crop" : "Plan a Crop"}</DialogTitle>
      <DialogContent>
        {/* Page 1: Plot, Date, Orientation */}
        {showPage1 && (
          <Box>
            <TextField
              fullWidth select
              label="Select Plot"
              name="plot_uuid"
              value={formState.plot_uuid}
              onChange={(e) => handlePlotChange(e.target.value)}
              margin="normal"
              className="planting-form-field"
            >
          <MenuItem value="">Select a plot</MenuItem>
          {selectionMode === "crop-first" && plotSuitability.length > 0 ? (
            // Show plots sorted alphabetically with rotation indicators
            [...plotSuitability]
              .sort((a, b) => a.plot.name.localeCompare(b.plot.name))
              .map((suitability) => {
                const plot = suitability.plot;
                return (
                  <MenuItem key={plot.plot_uuid} value={plot.plot_uuid}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{plot.name} ({(plot.width * plot.depth).toFixed(2)} m²)</span>
                      <SoilIndicator indicator={suitability.soil_indicator} />
                    </div>
                  </MenuItem>
                );
              })
          ) : (
            // Show plots in alphabetical order (plot-first mode)
            plots.filter((plot) => 
              plot.type !== "Building" && 
              plot.plot_type?.name?.toLowerCase() === 'vegetable bed'
            ).sort((a, b) => a.name.localeCompare(b.name)).map((plot) => (
              <MenuItem key={plot.plot_uuid} value={plot.plot_uuid}>
                {plot.name} ({(plot.width * plot.depth).toFixed(2)} m²)
              </MenuItem>
            ))
          )}
        </TextField>

        {/* Hidden date field - kept for form submission */}
        <input
          type="hidden"
          name="date_planted"
          value={formState.date_planted}
        />

        {/* Month and Year Selectors */}
        <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 1 }}>
          <TextField
            fullWidth
            select
            label="Month"
            value={getMonthFromDate(formState.date_planted)}
            onChange={(e) => handleMonthChange(e.target.value)}
            margin="normal"
            className="planting-form-field"
          >
            <MenuItem value="">Not selected</MenuItem>
            {monthNames.map((month, index) => (
              <MenuItem key={index} value={index}>
                {month}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            select
            label="Year"
            value={getYearFromDate(formState.date_planted)}
            onChange={(e) => handleYearChange(e.target.value)}
            margin="normal"
            className="planting-form-field"
          >
            {(() => {
              const currentYear = new Date().getFullYear();
              const existingYear = formState.date_planted 
                ? new Date(formState.date_planted).getFullYear()
                : null;
              const years = new Set();
              
              // Always include current year and next 2 years
              for (let i = 0; i < 3; i++) {
                years.add(currentYear + i);
              }
              
              // Include existing year if it's outside the default range
              if (existingYear && !years.has(existingYear)) {
                years.add(existingYear);
              }
              
              return Array.from(years).sort().map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ));
            })()}
          </TextField>
        </Box>

        {!plotHasCrops || isEditingOnlyCrop ? (
          <TextField
            fullWidth select
            label="Orientation"
            name="orientation"
            value={formState.orientation}
            onChange={handleChange}
            margin="normal"
            className="planting-form-field"
          >
            <MenuItem value="">Not set</MenuItem>
            <MenuItem value="lengthwise">Lengthwise</MenuItem>
            <MenuItem value="widthwise">Widthwise</MenuItem>
          </TextField>
        ) : (
          <>
            <TextField
              fullWidth
              label="Orientation"
              name="orientation"
              value={formState.orientation}
              margin="normal"
              className="planting-form-field"
              disabled
            />
            <TextField
              fullWidth select
              label="Placement Mode"
              name="placement_mode"
              value={formState.placement_mode}
              onChange={handleChange}
              margin="normal"
              className="planting-form-field"
            >
              <MenuItem value="next_to">Next To</MenuItem>
              <MenuItem value="between">Between</MenuItem>
            </TextField>
          </>
        )}
          </Box>
        )}

        {/* Page 2: Method, Amount, Search, Crop Table */}
        {showPage2 && (
          <Box>
            <TextField
              fullWidth select
              label="Method"
              name="method"
              value={formState.method}
              onChange={handleChange}
              margin="normal"
              className="planting-form-field"
            >
              <MenuItem value="">Not set</MenuItem>
              <MenuItem value="direct_sowing">Direct Sowing</MenuItem>
              <MenuItem value="planting">Planting</MenuItem>
              <MenuItem value="setting">Setting</MenuItem>
            </TextField>

            <TextField
              fullWidth
              label="Amount"
              name="amount"
              type="number"
              value={formState.amount}
              onChange={handleChange}
              margin="normal"
              className="planting-form-field"
              disabled={
                !plots.find((p) => p.plot_uuid === formState.plot_uuid) ||
                !crops.find((c) => c.crop_uuid === formState.crop_uuid) ||
                !formState.orientation
              }
              InputProps={{
                inputProps: { min: 1 }
              }}
              helperText={
                (() => {
                  const plot = plots.find((p) => p.plot_uuid === formState.plot_uuid);
                  const crop = crops.find((c) => c.crop_uuid === formState.crop_uuid);
                  
                  // Check if all required fields are present before calculating max
                  if (!plot || !crop || !formState.orientation) {
                    return ""; // Don't show error message since we're on page 2 and these should be filled
                  }
                  
                  const maxAmountData = getMaxAmount();
                  if (maxAmountData === null) {
                    return ""; // This shouldn't happen if plot, crop, and orientation are set
                  }
                  
                  // Show recommended max amount, but allow users to enter more
                  return `Recommended max ${maxAmountData.maxAmount} plants (${maxAmountData.equivalentRows} row${maxAmountData.equivalentRows !== 1 ? 's' : ''}) - you can enter more if needed`;
                })()
              }
            />

            <Typography variant="h6" gutterBottom>
              Select a Crop ({filteredCrops.length} available)
            </Typography>
            <TextField
              fullWidth
              label="Search Crops"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, type, or Latin name"
              variant="outlined"
              margin="normal"
              className="planting-form-field"
            />

        <TableContainer 
          component={Paper} 
          sx={{ 
            maxHeight: 400, 
            overflowY: 'auto',
            overflowX: 'auto',
            display: 'block',
            marginBottom: 2
          }}
        >
          <Table stickyHeader size="small" className="planting-crop-table">
            <TableHead>
              <TableRow>
                <TableCell className="column-name">Name</TableCell>
                <TableCell className="column-pre-germination">Pre-germination</TableCell>
                <TableCell className="column-direct-sowing">Direct Sowing</TableCell>
                <TableCell className="column-planting">Planting</TableCell>
                <TableCell className="column-growth-time">Growth Time</TableCell>
                <TableCell className="column-spacing">Spacing</TableCell>
                <TableCell className="column-grid">12-Month Grid</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCrops.map((crop) => {
                const preGerm = getWindowDates(crop, 'pre_germination');
                const sowing = getWindowDates(crop, 'direct_sowing');
                const planting = getWindowDates(crop, 'planting');
                const setting = getWindowDates(crop, 'setting');
                
                const ranges = {
                  preGerminationStart: preGerm.start,
                  preGerminationEnd: preGerm.end,
                  directSowingStart: sowing.start,
                  directSowingEnd: sowing.end,
                  plantingStart: planting.start,
                  plantingEnd: planting.end,
                  settingStart: setting.start,
                  settingEnd: setting.end,
                  harvestStart: null, // Harvest is calculated, not from windows
                  harvestEnd: null,
                };

                const growthTime =
                  crop.harvest_min_days && crop.harvest_max_days
                    ? crop.harvest_min_days === crop.harvest_max_days
                      ? `${crop.harvest_min_days} days`
                      : `${crop.harvest_min_days}-${crop.harvest_max_days} days`
                    : "N/A";

                const isSelected = crop.crop_uuid === formState.crop_uuid;
                
                // Get suitability data for this crop (for soil indicator only)
                const suitability = cropSuitability.find(s => s.crop.crop_uuid === crop.crop_uuid);

                return (
                  <TableRow
                    key={crop.crop_uuid}
                    hover
                    selected={isSelected}
                    onClick={() => handleCropSelect(crop)}
                    className={`crop-row ${isSelected ? 'crop-row-selected' : ''}`}
                  >
                    <TableCell className="column-name">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{crop.name}</span>
                        {suitability && (
                          <SoilIndicator indicator={suitability.soil_indicator} />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="column-pre-germination">
                      {ranges.preGerminationStart !== null &&
                      ranges.preGerminationEnd !== null
                        ? `${monthNames[ranges.preGerminationStart]} to ${monthNames[ranges.preGerminationEnd]}`
                        : "N/A"}
                    </TableCell>
                    <TableCell className="column-direct-sowing">
                      {ranges.directSowingStart !== null &&
                      ranges.directSowingEnd !== null
                        ? `${monthNames[ranges.directSowingStart]} to ${monthNames[ranges.directSowingEnd]}`
                        : "N/A"}
                    </TableCell>
                    <TableCell className="column-planting">
                      {ranges.plantingStart !== null &&
                      ranges.plantingEnd !== null
                        ? `${monthNames[ranges.plantingStart]} to ${monthNames[ranges.plantingEnd]}`
                        : "N/A"}
                    </TableCell>
                    <TableCell className="column-growth-time">{growthTime}</TableCell>
                    <TableCell className="column-spacing">
                      {crop.row_spacing_cm
                        ? `${crop.row_spacing_cm} cm between rows`
                        : "N/A"},{" "}
                      {crop.plant_spacing_cm
                        ? `${crop.plant_spacing_cm} cm in row`
                        : "N/A"}
                    </TableCell>
                    <TableCell className="column-grid">
                      <MonthBoxGrid ranges={ranges} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
          </Box>
        )}

        {/* Page 3: Summary (mobile only) */}
        {showPage3 && (
          <Box>
            {/* Summary box only on mobile pagination */}
            {showPagination && currentPage === 3 && (
              <>
                <Typography variant="h6" gutterBottom>
                  Review Your Selection
                </Typography>
                <Box 
                  sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: 'rgba(184, 168, 138, 0.1)', 
                    border: '1px solid var(--color-border-vintage)',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'var(--color-sepia)' }}>
                    {getSummaryText() || "Please complete all previous steps"}
                  </Typography>
                </Box>
              </>
            )}

          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {isEditMode && <Button onClick={handleDelete} color="secondary">Delete</Button>}
        <Button onClick={onClose} color="secondary">Cancel</Button>
        
        {/* Pagination buttons for mobile */}
        {showPagination && currentPage === 1 && (
          <Button
            onClick={() => setCurrentPage(2)}
            color="primary"
            variant="contained"
            disabled={!isPage1Complete()}
          >
            Next
          </Button>
        )}
        
        {showPagination && currentPage === 2 && (
          <>
            <Button
              onClick={() => setCurrentPage(1)}
              color="secondary"
            >
              Previous
            </Button>
            <Button
              onClick={() => setCurrentPage(3)}
              color="primary"
              variant="contained"
              disabled={!isPage2Complete()}
            >
              Next
            </Button>
          </>
        )}

        {showPagination && currentPage === 3 && (
          <>
            <Button
              onClick={() => setCurrentPage(2)}
              color="secondary"
            >
              Previous
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={!isFormValid()}
            >
              Plan Crop
            </Button>
          </>
        )}
        
        {/* Desktop or edit mode: show submit button */}
        {!showPagination && (
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={!isFormValid()}
          >
            {isEditMode ? "Update Crop" : "Plan Crop"}
          </Button>
        )}
      </DialogActions>
    </Dialog>

    </>
  );
};

export default PlantingForm;
